import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useLanguage, apiClient } from "../App";
import PageHeader from "../components/layout/PageHeader";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { User, Mail, Phone, User2 } from "lucide-react";
import CharacterCarousel from "../components/characters/CharacterCarousel";
import CharacterInfo from "../components/characters/CharacterInfo";
import CharacterActionBar from "../components/characters/CharacterActionBar";
import { getCharactersByGender } from "../config/characters";
import { toast } from "sonner";

const CharacterSelect = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { language } = useLanguage();
  const isEnglish = language === "en";

  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    gender: user?.gender || ""
  });
  const characters = useMemo(() => getCharactersByGender(profileData.gender || user?.gender), [profileData.gender, user?.gender]);
  const [selectedId, setSelectedId] = useState(user?.avatar_character_id || (characters[0]?.id || null));
  const selectedChar = useMemo(() => characters.find(c => c.id === selectedId), [characters, selectedId]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selectedId && characters.length > 0) {
      setSelectedId(characters[0].id);
    }
  }, [characters, selectedId]);

  const onConfirm = async () => {
    try {
      if (!selectedId) return;
      setSaving(true);
      const updateData = {};
      if (profileData.name !== user?.name) updateData.name = profileData.name.trim();
      if (profileData.email !== user?.email) updateData.email = profileData.email.trim();
      if (profileData.phone !== user?.phone) updateData.phone = profileData.phone.trim() || null;
      if (profileData.gender !== user?.gender) updateData.gender = profileData.gender || null;
      if (selectedId !== user?.avatar_character_id) updateData.avatar_character_id = selectedId;

      if (Object.keys(updateData).length > 0) {
        await apiClient.patch("/auth/me", updateData);
        setUser({ ...user, ...updateData });
      }
      toast.success(isEnglish ? "Profile updated" : "Profil diperbarui");
      navigate("/profile");
    } catch (e) {
      const msg = e.response?.data?.detail || (isEnglish ? "Failed to save" : "Gagal menyimpan");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen">
      <PageHeader title={isEnglish ? "Choose Character" : "Pilih Karakter"} showBack />
      <div className="page-container space-y-4">
        <div className="rounded-3xl bg-gradient-to-b from-blue-600 to-indigo-600 text-white px-4 py-5 sm:px-6 sm:py-6">
          <h2 className="text-base sm:text-lg font-bold">{isEnglish ? "Hand-pick Your Character" : "Pilih Karakter Anda"}</h2>
          <p className="text-xs sm:text-sm text-white/80 mt-1">{isEnglish ? "Swipe to explore and tap to select" : "Geser untuk jelajahi dan ketuk untuk memilih"}</p>
        </div>

        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="mb-3 sm:mb-4">
              <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-950/20 dark:to-blue-950/20 rounded-lg sm:rounded-xl border border-indigo-200 dark:border-indigo-900">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-md sm:shadow-lg">
                  <User2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold truncate">{isEnglish ? "Profile Data" : "Data Profil"}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">{isEnglish ? "Update your information" : "Perbarui informasi Anda"}</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
              <div className="space-y-1.5">
                <Label htmlFor="ep-name" className="text-sm">{isEnglish ? "Name" : "Nama"} *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="ep-name" value={profileData.name} onChange={(e) => setProfileData({ ...profileData, name: e.target.value })} className="text-sm pl-9 h-10 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ep-email" className="text-sm">{isEnglish ? "Email" : "Email"} *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="ep-email" type="email" value={profileData.email} onChange={(e) => setProfileData({ ...profileData, email: e.target.value })} className="text-sm pl-9 h-10 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ep-phone" className="text-sm">{isEnglish ? "Phone" : "Telepon"}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="ep-phone" type="tel" value={profileData.phone} onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })} className="text-sm pl-9 h-10 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ep-gender" className="text-sm">{isEnglish ? "Gender" : "Jenis Kelamin"}</Label>
                <Select value={profileData.gender} onValueChange={(value) => setProfileData({ ...profileData, gender: value })}>
                  <SelectTrigger id="ep-gender" className="text-sm h-10 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500">
                    <SelectValue placeholder={isEnglish ? "Select gender" : "Pilih jenis kelamin"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male" className="text-sm">{isEnglish ? "Male" : "Pria"}</SelectItem>
                    <SelectItem value="female" className="text-sm">{isEnglish ? "Female" : "Wanita"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <CharacterCarousel characters={characters} selectedId={selectedId} onSelect={setSelectedId} />
            <CharacterInfo character={selectedChar} />
            <CharacterActionBar onConfirm={onConfirm} disabled={saving || !selectedId} label={isEnglish ? "Save" : "Simpan"} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CharacterSelect;
